import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  BRAND,
  SERVICES,
  CREDENTIALS,
  WHY_POINTS,
  PROCESS_STEPS,
} from "@/lib/content";
import SiteLayout from "@/components/site/SiteLayout";

function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-brand-cream">
      <div className="container-narrow relative pt-16 pb-24 md:pt-28 md:pb-36">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
          <div className="col-span-12 lg:col-span-8 animate-fade-up">
            <div className="overline mb-8" data-testid="hero-overline">
              AssocRICS · MCIOB · BDMA.Tech
            </div>
            <h1
              data-testid="hero-headline"
              className="font-serif text-[44px] sm:text-[64px] lg:text-[84px] xl:text-[96px] leading-[0.95] tracking-tight text-brand-navy"
            >
              Independent
              <br />
              property advice,
              <br />
              <span className="italic font-light text-brand-navy/85">written for </span>
              <span className="italic font-light text-brand-champagne">the people who use it.</span>
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-10 max-w-xl text-base md:text-lg text-brand-navy/70 leading-relaxed"
            >
              Accredited surveys, EPCs and bespoke property reports throughout Lincolnshire
              and the surrounding areas. Plain-English findings, photographed evidence and a
              professional opinion you can rely on.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
              >
                Book a Survey
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream hover:border-brand-navy transition-colors"
              >
                Explore Services
              </Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l lg:border-brand-navy/10 flex flex-col justify-end animate-fade-up">
            <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-brand-champagne mb-6">
              Service Coverage
            </div>
            <div className="font-serif text-5xl md:text-6xl text-brand-navy leading-none">
              Lincolnshire
            </div>
            <p className="mt-4 text-sm text-brand-navy/65 leading-relaxed max-w-xs">
              Lincoln, Grantham, Sleaford, Boston, Skegness, Spalding, Louth, Bourne, Horncastle
              and the surrounding towns. Same-week availability for EPCs, snagging and condition
              surveys.
            </p>
            <div className="mt-10 pt-8 border-t border-brand-navy/10 grid grid-cols-3 gap-4">
              <Stat number="15+" label="Years in the trade" />
              <Stat number="3" label="Accreditations" />
              <Stat number="48h" label="Avg. booking lead" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-serif text-3xl md:text-4xl text-brand-navy">{number}</div>
      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-brand-navy/55 font-medium leading-snug">
        {label}
      </div>
    </div>
  );
}

function CredentialsStrip() {
  return (
    <section
      data-testid="credentials-strip"
      className="border-y border-brand-navy/10 bg-white"
    >
      <div className="container-narrow py-10 md:py-14 grid grid-cols-2 md:grid-cols-4 items-center gap-8">
        <div className="md:col-span-1">
          <div className="overline">Accreditations</div>
          <div className="mt-2 font-serif text-2xl text-brand-navy">Verified by industry</div>
        </div>
        {CREDENTIALS.map((c) => (
          <div
            key={c.code}
            data-testid={`credential-${c.code}`}
            className="flex flex-col items-start md:items-start"
          >
            <div className="font-serif text-3xl md:text-4xl text-brand-navy tracking-tight">
              {c.code}
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-navy/55 mt-2 leading-snug">
              {c.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-brand-cream"
    >
      <div className="container-narrow py-24 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-12 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <div className="overline">Our Services</div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-brand-navy leading-[1.02] tracking-tight">
              Surveys, reports &amp; advice.<br />
              <span className="italic font-light">One trusted consultant.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-brand-navy/70 leading-relaxed">
              Every service is delivered personally by Declan — an AssocRICS, MCIOB and
              BDMA.Tech accredited consultant — and produced as a photographed, plain-English
              report that buyers, lenders and solicitors can act on.
            </p>
          </div>
        </div>

        <div className="border-t border-l border-brand-navy/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services#${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group relative p-8 md:p-10 border-r border-b border-brand-navy/10 bg-white hover:bg-brand-navy hover:text-brand-cream transition-colors duration-500 min-h-[320px] flex flex-col"
            >
              <div className="flex items-start justify-between">
                <span className="font-serif text-2xl text-brand-champagne">{s.number}</span>
                <ArrowUpRight
                  size={20}
                  className="text-brand-navy/40 group-hover:text-brand-cream transition-colors"
                />
              </div>
              <h3 className="mt-10 font-serif text-2xl md:text-[28px] leading-tight tracking-tight">
                {s.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-brand-navy/70 group-hover:text-brand-cream/75 transition-colors">
                {s.short}
              </p>
              <div className="mt-auto pt-8 text-[10px] tracking-[0.22em] uppercase font-semibold text-brand-champagne">
                Read more
              </div>
            </Link>
          ))}
          {/* Filler tile for grid balance */}
          <div className="hidden lg:flex p-10 border-r border-b border-brand-navy/10 bg-brand-surface/40 flex-col justify-between min-h-[320px]">
            <div className="overline">Need something else?</div>
            <div>
              <p className="font-serif text-2xl text-brand-navy leading-tight">
                Bespoke property advice tailored to your circumstances.
              </p>
              <Link
                to="/contact"
                data-testid="services-grid-cta"
                className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link"
              >
                Discuss your project
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DeepServices() {
  return (
    <section
      data-testid="deep-services"
      className="bg-white border-t border-brand-navy/10"
    >
      <div className="container-narrow py-24 md:py-32">
        <div className="overline">In Detail</div>
        <h2 className="mt-6 font-serif text-4xl md:text-5xl text-brand-navy leading-tight tracking-tight max-w-3xl">
          Every report is built around the question you actually need answered.
        </h2>

        <div className="mt-20 space-y-24">
          {SERVICES.map((s, i) => (
            <article
              key={s.slug}
              id={s.slug}
              data-testid={`deep-service-${s.slug}`}
              className="grid grid-cols-12 gap-y-8 md:gap-x-12"
            >
              <div className="col-span-12 md:col-span-4">
                <div className="font-serif text-5xl md:text-6xl text-brand-champagne">
                  {s.number}
                </div>
                <h3 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                  {s.title}
                </h3>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.audience.map((a) => (
                    <span
                      key={a}
                      className="text-[10px] tracking-[0.22em] uppercase border border-brand-navy/15 text-brand-navy/70 px-3 py-1.5"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
              <div className="col-span-12 md:col-span-8 md:pl-10 md:border-l md:border-brand-navy/10">
                <p className="text-base md:text-lg text-brand-navy/75 leading-relaxed">
                  {s.long}
                </p>
                <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {s.benefits.map((b, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-sm text-brand-navy/85 leading-relaxed"
                    >
                      <span className="mt-2 inline-block w-2 h-px bg-brand-champagne flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`deep-service-cta-${s.slug}`}
                  className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link"
                >
                  Request this service
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section data-testid="why-section" className="bg-brand-navy text-brand-cream">
      <div className="container-narrow py-24 md:py-32">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-12">
          <div className="col-span-12 md:col-span-5">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              Why Clients Choose Us
            </div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              A quieter, more
              <br />
              <span className="italic font-light text-brand-champagne">considered approach</span>
              <br />
              to property.
            </h2>
            <p className="mt-8 text-brand-cream/70 text-base md:text-lg leading-relaxed max-w-md">
              We work with a small number of clients at any one time. That means more
              attention, more depth, and a report that actually helps you make the right
              decision.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-cream/10">
            {WHY_POINTS.map((p) => (
              <div
                key={p.n}
                data-testid={`why-point-${p.n}`}
                className="bg-brand-navy p-8 md:p-10"
              >
                <div className="font-serif text-3xl text-brand-champagne">{p.n}</div>
                <h3 className="mt-4 font-serif text-2xl text-brand-cream leading-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-brand-cream/70 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section data-testid="process-section" className="bg-brand-cream">
      <div className="container-narrow py-24 md:py-32">
        <div className="overline">Our Process</div>
        <h2 className="mt-6 font-serif text-4xl md:text-5xl text-brand-navy leading-tight tracking-tight max-w-2xl">
          Four steps from your first enquiry to a signed-off report.
        </h2>
        <div className="mt-16 border-t border-brand-navy/10">
          {PROCESS_STEPS.map((s) => (
            <div
              key={s.step}
              data-testid={`process-step-${s.step}`}
              className="grid grid-cols-12 gap-6 py-10 border-b border-brand-navy/10"
            >
              <div className="col-span-3 md:col-span-2 font-serif text-3xl text-brand-champagne">
                {s.step}
              </div>
              <div className="col-span-9 md:col-span-4">
                <h3 className="font-serif text-2xl text-brand-navy">{s.title}</h3>
              </div>
              <div className="col-span-12 md:col-span-6 text-brand-navy/70 text-base leading-relaxed">
                {s.body}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section data-testid="final-cta" className="bg-white border-t border-brand-navy/10">
      <div className="container-narrow py-24 md:py-32">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-12 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline">Get In Touch</div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-brand-navy leading-[1.02] tracking-tight">
              Talk to Declan about
              <br />
              <span className="italic font-light text-brand-champagne">your property.</span>
            </h2>
            <p className="mt-8 max-w-xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
              Whether you&apos;re buying, letting or signing off a new build, a short conversation
              is the fastest way to know what&apos;s worth doing next.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="final-cta-button"
              className="inline-flex items-center justify-between gap-3 bg-brand-navy text-brand-cream px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Request a Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="final-cta-call"
              className="inline-flex items-center justify-between gap-3 border border-brand-navy/30 text-brand-navy px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <SiteLayout
      pageTitle="Independent Property Consultant in Lincolnshire"
      pageDescription="Declan Bishop — independent property consultant serving Lincoln, Grantham, Sleaford, Boston, Skegness, Spalding, Louth, Bourne, Horncastle and throughout Lincolnshire. EPCs, landlord inspections, condition surveys, snagging, defect reports, roof surveys and floor plans. AssocRICS · MCIOB · BDMA.Tech."
      pagePath="/"
    >
      <Hero />
      <CredentialsStrip />
      <ServicesGrid />
      <DeepServices />
      <WhySection />
      <ProcessSection />
      <FinalCTA />
    </SiteLayout>
  );
}
